/**
 * Navbar — the storefront header, Blinkit-style, on a clean cream theme.
 *
 * SCROLL BEHAVIOUR (home):
 *   LOCATION BAR   brand · deliver-to · profile   → scrolls AWAY
 *   ┈ sentinel ┈   (marks where the sticky block pins)
 *   SEARCH + CATEGORIES  → PIN to the top as one sticky container, and gain a
 *                          soft shadow once pinned (via useStuck).
 *   HERO           tagline + scooter over a faint campus skyline → scrolls
 *                  up and disappears beneath the pinned search + categories.
 *
 * Only the search bar and category strip stay on screen while scrolling, with
 * the categories directly under the search (no gap) — exactly like the mock.
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Mic,
  MapPin,
  ChevronDown,
  User,
  Clock,
  LayoutGrid,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useCollege } from '@/context/CollegeContext';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { useCategories } from '@/hooks/useStorefront';
import { useStuck } from '@/hooks/useStuck';
import { cn } from '@/lib/cn';
import { categoryIcon, categoryShortLabel } from '@/utils/categoryIcon';
import { CategoryScroller } from '@/components/CategoryScroller';
import { CampusWatermark, DeliveryScooter } from './HeroArt';
import { DeliveryLocationPicker } from '@/components/DeliveryLocationPicker';

export function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const { college, collegeId } = useCollege();
  const { customer } = useCustomerAuth();
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const { data: liveCategories = [] } = useCategories(collegeId);
  const { sentinelRef, stuck } = useStuck();

  const isHome = location.pathname === '/';
  const isCategories = location.pathname.startsWith('/categories');

  // The category strip shows on home AND the categories page; on categories it
  // highlights whichever category the URL has open (?category=<id>).
  const categoryParam = new URLSearchParams(location.search).get('category');
  const activeCategoryId = isCategories ? (categoryParam ?? 'all') : 'all';

  // Real categories → the icon-based tabs the scroller expects. "All" leads.
  const categoryTabs = [
    { id: 'all', label: 'All', icon: LayoutGrid },
    ...liveCategories.map((c) => ({
      id: c._id,
      label: categoryShortLabel(c.name),
      icon: categoryIcon(c.name),
    })),
  ];

  // Tapping a category opens the categories page filtered to it.
  const onSelectCategory = (id) => {
    navigate(id === 'all' ? ROUTES.categories : `${ROUTES.categories}?category=${id}`);
  };

  const onSearch = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed) navigate(`${ROUTES.search}?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <>
      {/* ===================== LOCATION BAR — scrolls away ===================== */}
      <div className="bg-cream text-ink">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 pb-2 pt-3">
          <Link to={ROUTES.home} className="flex shrink-0 items-center gap-1.5">
            {/*
              The mark's corners are transparent in the PNG itself, not clipped
              by CSS — the source art sat on an opaque near-white square, so a
              border-radius here would still have shown white pixels wherever
              the two radii disagreed.
            */}
            <img
              src="/brand/rocket07-mark.png"
              alt="Rocket07"
              width={40}
              height={40}
              className="animate-float h-10 w-10 shrink-0 object-contain"
            />
            <span className="hidden text-lg font-extrabold tracking-tight text-ink sm:block">
              Rocket<span className="text-primary-700">07</span>
            </span>
          </Link>

          {isCategories ? (
            <h1 className="min-w-0 flex-1 truncate text-lg font-extrabold">All Categories</h1>
          ) : (
            <button
              type="button"
              onClick={() => setLocationPickerOpen(true)}
              className="group flex min-w-0 flex-1 items-center gap-1.5 text-left"
              aria-label="Change delivery location"
            >
              <MapPin size={16} className="mt-0.5 shrink-0 self-start text-ink/70" />
              <span className="min-w-0">
                <span className="block text-[11px] font-medium leading-none text-ink/60">
                  Deliver to
                </span>
                <span className="mt-0.5 flex items-center gap-0.5 text-[15px] font-extrabold">
                  {/* Campus is fixed; the hostel is what this button changes,
                      so show it here once chosen. */}
                  <span className="truncate">
                    {customer?.defaultLocation?.name ?? college?.name ?? 'Your campus'}
                  </span>
                  <ChevronDown
                    size={16}
                    className="shrink-0 transition-transform duration-200 group-hover:translate-y-0.5"
                  />
                </span>
              </span>
            </button>
          )}

          {/*
            No cart here: BottomNav already owns it. Two carts on one screen
            means two places to keep in sync and two answers to "where is my
            cart", so the one that is always visible wins.
          */}
          <Link
            to={ROUTES.profile}
            aria-label="Profile"
            className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-white text-ink shadow-sm transition-transform duration-200 hover:scale-105 active:scale-95"
          >
            {customer?.avatar ? (
              <img
                src={customer.avatar}
                alt=""
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <User size={20} />
            )}
          </Link>
        </div>
      </div>

      {/* Sentinel: as it leaves the top, the block below has pinned. */}
      <div ref={sentinelRef} aria-hidden="true" className="h-px bg-cream" />

      {/* ============== SEARCH + CATEGORIES — one sticky container ============== */}
      <div
        className={cn(
          'sticky top-0 z-40 bg-cream transition-shadow duration-200',
          stuck && 'shadow-[0_6px_18px_-10px_rgba(20,20,20,0.35)]',
        )}
      >
        {/* Search bar */}
        <div className="mx-auto max-w-6xl px-4 pb-2 pt-2.5">
          <form onSubmit={onSearch}>
              <div className="relative">
                <Search
                  size={20}
                  strokeWidth={2.5}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-ink"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search for products…"
                  aria-label="Search products"
                  className="h-12 w-full rounded-xl border-0 bg-white pl-12 pr-14 text-sm text-ink shadow-md placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent-400"
                />
                <span
                  aria-hidden="true"
                  className="absolute right-12 top-1/2 h-5 w-px -translate-y-1/2 bg-line"
                />
                <button
                  type="button"
                  aria-label="Voice search (coming soon)"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/70 transition-transform duration-200 hover:scale-110 active:scale-90"
                >
                  <Mic size={18} />
                </button>
              </div>
            </form>
          </div>

        {/* Categories — directly below the search bar, no gap. Shown on home and
            the categories page; tapping one opens that category's products. */}
        {(isHome || isCategories) && liveCategories.length > 0 && (
          <CategoryScroller
            categories={categoryTabs}
            activeId={activeCategoryId}
            onSelect={onSelectCategory}
          />
        )}
      </div>

      {/* ===================== HERO — scrolls under the header ===================== */}
      {isHome && (
        <section className="relative overflow-hidden bg-gradient-to-b from-cream via-[#fbe4a0] to-[#f7cf63] text-ink">
          <CampusWatermark college={college} />
          <div className="relative mx-auto flex max-w-6xl items-end justify-between gap-1 px-4 pb-6 pt-4">
            <div className="animate-rise min-w-0 flex-1">
              <h2 className="text-[19px] font-extrabold leading-snug text-ink">
                Fresh groceries,
                <br />
                delivered to your hostel
              </h2>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-ink shadow-sm backdrop-blur-sm">
                <Clock size={14} className="text-primary-600" />
                Evening delivery • 6–7 PM
              </span>
            </div>

            <DeliveryScooter className="animate-float h-28 w-auto shrink-0 self-end drop-shadow-sm" />
          </div>
        </section>
      )}

      <DeliveryLocationPicker
        isOpen={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
      />
    </>
  );
}

export default Navbar;
